/**
 * Path-mode terrain-profile offsets.
 *
 * Reuses the global distance view's "Terrengprofiler" feature (offset lines N m
 * above the ground profile) inside path-mode. The `/info` (and
 * `/calculate`) response embeds `TerrainProfile:…` packets in every load case's
 * CZML — identical across load cases — so we take the FIRST load case as the
 * reference, load its terrain-profile polylines into the SHARED
 * `distanceAnalysisTerrainProfilesDataSource` (the same datasource the global
 * mode uses), and let `createTerrainOffsets` generate the offset lines from the
 * user's offset selections.
 *
 * Driven by `pathModeState.showDistances`: profiles load when "Vis avstander"
 * is on and clear when it's off or path-mode exits. The global distance view is
 * mutually exclusive with path-mode, so nothing else touches the shared
 * datasource while path-mode owns it.
 */

import { useCesiumViewer } from '../cesium-viewer';
import { useDataSources } from '../cesium-datasources';
import { createTerrainOffsets } from '../map-utils';
import { pathModeState } from './path-mode-state.svelte';
import { disableDistanceDisplayCondition } from './pathToolLoadCases.svelte';

const TERRAIN_PROFILE_PREFIX = 'TerrainProfile:';

/**
 * Load the reference load case's terrain profiles into the shared datasource
 * and (re)generate the offset lines for the currently-active offsets.
 */
async function applyPathTerrainProfiles(): Promise<void> {
	const viewer = useCesiumViewer();
	if (!viewer) return;
	const { distanceAnalysisTerrainProfilesDataSource: ds } = useDataSources();
	// Datasources are created lazily with the viewer; the module-level effect
	// can fire before that, so bail until they exist.
	if (!ds) return;

	// All load cases carry identical TerrainProfile packets — use the first.
	const referenceCzml = pathModeState.loadCases[0]?.czml ?? [];
	const documentPacket =
		referenceCzml.find((p) => p?.id === 'document') ?? { id: 'document', version: '1.0' };
	const profilePackets = referenceCzml.filter(
		(p) => typeof p?.id === 'string' && p.id.startsWith(TERRAIN_PROFILE_PREFIX)
	);

	if (!viewer.dataSources.contains(ds)) await viewer.dataSources.add(ds);

	// `load` replaces the datasource contents (including any stale offset clones
	// from a previous run), leaving a clean set of base profiles to offset from.
	await ds.load([documentPacket, ...profilePackets]);

	// createTerrainOffsets only clones from visible base profiles. Also strip
	// the server's `distanceDisplayCondition` so the profiles (and the offset
	// clones, handled in createTerrainOffsets) stay visible at any zoom.
	for (const entity of ds.entities.values) entity.show = true;
	disableDistanceDisplayCondition(ds.entities.values);

	await createTerrainOffsets();
}

/** Empty the shared terrain-profile datasource (base profiles + offsets). */
function clearPathTerrainProfiles(): void {
	const { distanceAnalysisTerrainProfilesDataSource: ds } = useDataSources();
	// May be undefined when the effect fires before the viewer is initialised.
	if (!ds) return;
	ds.entities.removeAll();
}

// ── Reactive driver ──────────────────────────────────────────────────────────
// Load/clear profiles as "Vis avstander" toggles, the load-case set changes
// (recalc), or path-mode enters/exits. Offset on/off changes do NOT flow
// through here — the panel's "Oppdater terrengprofiler" button calls
// createTerrainOffsets directly, reading the offset config non-reactively.

let lastKey = '';

$effect.root(() => {
	$effect(() => {
		const active = pathModeState.active;
		const showDistances = pathModeState.showDistances;
		const loadCases = pathModeState.loadCases;
		const wants = active && showDistances && loadCases.length > 0;

		// Re-key on the reference load case + source so a recalc that swaps the
		// load-case set reloads the profiles (geometry is bound to the selected masts and
		// usually unchanged, but this keeps it robust and cheap).
		const key = wants
			? `on|${loadCases[0]?.name ?? ''}|${pathModeState.loadCaseSource ?? ''}`
			: 'off';
		if (key === lastKey) return;
		lastKey = key;

		if (wants) void applyPathTerrainProfiles();
		else clearPathTerrainProfiles();
	});
});
